import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import { FailureNotice, LoadingNotice } from './AsyncState.tsx';

/**
 * The loading/error/empty/content chain a section built around one `useQuery` needs, in one place.
 *
 * First written for `routes/PatientDermatologyPage.tsx`'s own four sections (found in review
 * 2026-08-18 there: all four had reimplemented the identical four-way ternary by hand). This is a
 * second, independent extraction here in `components/` rather than a second copy — `routes/
 * PatientAestheticsPage.tsx`'s own five sections are the second real caller of the exact same shape,
 * which is `claude-kit/rules/invariants.md`'s "favor the general, reusable solution once a pattern
 * actually recurs" applied one level up: not just deduplicating within one file, but making the
 * pattern itself reusable across specialty modules. `PatientDermatologyPage.tsx`'s own inline
 * `QuerySection` is left as-is rather than retrofitted to import this one — that file is merged and
 * owned by nobody actively working on it right now, and editing it is out of scope for this module
 * (per this task's own brief); a future pass that touches that file is the right place to collapse
 * the duplication this comment names.
 *
 * `isEmpty` defaults to "never empty" for a section whose query returns a single record rather than
 * a list, matching the original's own reasoning.
 */
export function QuerySection<T>({
  query,
  loadingLabel,
  emptyMessage,
  isEmpty = () => false,
  children,
}: {
  readonly query: UseQueryResult<T>;
  readonly loadingLabel: string;
  readonly emptyMessage?: string;
  readonly isEmpty?: (data: T) => boolean;
  readonly children: (data: T) => ReactNode;
}) {
  if (query.isPending) return <LoadingNotice label={loadingLabel} />;
  if (query.isError) {
    return (
      <FailureNotice
        error={query.error}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  if (emptyMessage !== undefined && isEmpty(query.data)) {
    return <p className="m-0 text-sm text-ink-muted">{emptyMessage}</p>;
  }
  return <>{children(query.data)}</>;
}
